import type { LatLon } from "@radar-sim/schema";
import { bearingDeg, distanceNm } from "@radar-sim/engine";

/**
 * Radial nearest-land occlusion model for the PPI. Real radar can't
 * see behind land — the beam hits the first reflector and everything
 * beyond is in shadow. This helper pre-computes, for each compass
 * bearing bin, the distance to the closest piece of coastline. PPI
 * draw passes (coastline outline, marks, etc.) then ask "is point P
 * the closest land at its bearing?" — if not, P sits in shadow.
 *
 * The shape was chosen for cost-per-frame: O(edges + bins) prep
 * instead of O(edges²) pairwise visibility, so a UK-wide coastline
 * (~30k points) is still cheap per redraw at 24 nm range.
 */
export interface VisibilityField {
  /** Distance in nm to the nearest land at `brgDeg` from own. Returns
   *  Infinity when no land is registered in that bearing bin. */
  nearestNm(brgDeg: number): number;
  /** True if `p` is the visible foremost point along its bearing
   *  (i.e. closer to own than any other land at the same bearing). */
  isVisible(p: LatLon): boolean;
}

const TOLERANCE_NM = 0.04; // ~75 m — softens floating-point edges

export function buildVisibilityField(
  own: LatLon,
  coastline: ReadonlyArray<ReadonlyArray<LatLon>>,
  maxRangeNm: number,
  binSize = 0.5,
): VisibilityField {
  const bins = Math.round(360 / binSize);
  const nearest = new Float64Array(bins);
  nearest.fill(Number.POSITIVE_INFINITY);
  // Two-step pad: anything beyond 1.2× the radar range can't shadow
  // a point on screen so we skip it. Avoids per-pixel cost when the
  // user is in mid-Solent and the UK-wide layer is loaded.
  const radius2 = (maxRangeNm * 1.2) * (maxRangeNm * 1.2);
  // Approximate planar conversion for the range gate.
  const cosLat = Math.cos((own.lat * Math.PI) / 180);
  for (const ring of coastline) {
    for (let i = 0; i < ring.length - 1; i++) {
      const a = ring[i]!;
      const b = ring[i + 1]!;
      const aDLat = (a.lat - own.lat) * 60;
      const aDLon = (a.lon - own.lon) * 60 * cosLat;
      const bDLat = (b.lat - own.lat) * 60;
      const bDLon = (b.lon - own.lon) * 60 * cosLat;
      if (
        aDLat * aDLat + aDLon * aDLon > radius2 &&
        bDLat * bDLat + bDLon * bDLon > radius2
      ) {
        continue;
      }
      // Sample along the segment at angular resolution finer than the
      // bin size, so a long segment isn't skipped over a bin boundary.
      const brgA = bearingDeg(own, a);
      const brgB = bearingDeg(own, b);
      const span = ((brgB - brgA + 540) % 360) - 180; // signed shortest
      const steps = Math.max(
        2,
        Math.ceil(Math.abs(span) / (binSize * 0.5)),
      );
      for (let s = 0; s <= steps; s++) {
        const t = s / steps;
        const lat = a.lat + (b.lat - a.lat) * t;
        const lon = a.lon + (b.lon - a.lon) * t;
        const p = { lat, lon };
        const d = distanceNm(own, p);
        if (d > maxRangeNm * 1.2) continue;
        const brg = ((brgA + span * t) % 360 + 360) % 360;
        const bin = Math.floor(brg / binSize);
        if (d < nearest[bin]!) nearest[bin] = d;
      }
    }
  }
  const nearestAt = (brgDeg: number): number => {
    const brg = ((brgDeg % 360) + 360) % 360;
    const bin = Math.floor(brg / binSize);
    // Take min of the bin and its neighbours — softens the bearing
    // quantisation so a coastline point near a bin boundary isn't
    // accidentally shadowed by its own bin's slot for an adjacent
    // (closer) feature.
    const left = nearest[(bin + bins - 1) % bins]!;
    const here = nearest[bin]!;
    const right = nearest[(bin + 1) % bins]!;
    return Math.min(left, here, right);
  };
  return {
    nearestNm: nearestAt,
    isVisible(p: LatLon): boolean {
      const brg = bearingDeg(own, p);
      const d = distanceNm(own, p);
      return d <= nearestAt(brg) + TOLERANCE_NM;
    },
  };
}
